/* WD CUSTOM VERSION - SAFE TO EDIT
 * ----------------------------------------------------------------------------
 * All custom JS for MVD Racer goes here.
 * Loaded globally from layout/theme.liquid as an ES module AFTER sections.js.
 *
 * Conventions:
 *   - Register new custom elements with the wd- prefix:
 *       customElements.define('wd-hero-cta', WdHeroCta);
 *   - Guard registration to avoid double-defining on soft navigations:
 *       if (!window.customElements.get('wd-foo')) { ... }
 *   - Access theme variables via window.themeVariables (set by js-variables.liquid)
 *   - Never edit assets/theme.js, assets/sections.js, or assets/vendor.min.js
 * ----------------------------------------------------------------------------
 */

/* ============================================================================
 * <wd-reviews-slider> — sections/wd-google-reviews.liquid
 * Autoplay + touch-swipe + arrows + dots horizontal scroll-snap slider.
 * Pure native scroll; no third-party deps.
 * ========================================================================== */
class WdReviewsSlider extends HTMLElement {
  constructor() {
    super();
    this._onScroll = this._onScroll.bind(this);
    this._onResize = this._onResize.bind(this);
    this._onPointerEnter = () => this._pause();
    this._onPointerLeave = () => this._resume();
    this._onVisibility = () => (document.hidden ? this._pause() : this._resume());
    this._inView = true;
  }

  connectedCallback() {
    this.track = this.querySelector('[data-wd-track]');
    if (!this.track) return;

    this.slides = Array.from(this.querySelectorAll('[data-wd-slide]'));
    if (this.slides.length === 0) return;

    this.prevBtn = this.querySelector('[data-wd-prev]');
    this.nextBtn = this.querySelector('[data-wd-next]');
    this.dotsHost = this.querySelector('[data-wd-dots]');

    this.autoplay = this.dataset.wdAutoplay === 'true';
    this.delay = parseInt(this.dataset.wdDelay, 10) || 5000;

    this._buildDots();
    this._bind();
    this._update();
    this._observeVisibility();
    if (this.autoplay) this._resume();
  }

  disconnectedCallback() {
    this._pause();
    this.track?.removeEventListener('scroll', this._onScroll);
    window.removeEventListener('resize', this._onResize);
    this.removeEventListener('pointerenter', this._onPointerEnter);
    this.removeEventListener('pointerleave', this._onPointerLeave);
    this.removeEventListener('focusin', this._onPointerEnter);
    this.removeEventListener('focusout', this._onPointerLeave);
    document.removeEventListener('visibilitychange', this._onVisibility);
    this._io?.disconnect();
  }

  _bind() {
    this.track.addEventListener('scroll', this._onScroll, { passive: true });
    window.addEventListener('resize', this._onResize, { passive: true });
    this.prevBtn?.addEventListener('click', () => { this._userInteract(); this._step(-1); });
    this.nextBtn?.addEventListener('click', () => { this._userInteract(); this._step(1); });
    // Pointer events normalize mouse + touch + pen — touchstart/touchend would
    // double-fire on mobile and could leave autoplay paused after a tap.
    this.addEventListener('pointerenter', this._onPointerEnter);
    this.addEventListener('pointerleave', this._onPointerLeave);
    this.addEventListener('focusin', this._onPointerEnter);
    this.addEventListener('focusout', this._onPointerLeave);
    document.addEventListener('visibilitychange', this._onVisibility);
  }

  _buildDots() {
    if (!this.dotsHost) return;
    this.dotsHost.innerHTML = '';
    this.slides.forEach((_, i) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'wd-google-reviews__dot';
      dot.setAttribute('aria-label', `Go to review ${i + 1}`);
      dot.addEventListener('click', () => { this._userInteract(); this._goTo(i); });
      this.dotsHost.appendChild(dot);
    });
    this.dots = Array.from(this.dotsHost.children);
  }

  _slideStride() {
    if (this.slides.length < 2) return this.slides[0]?.getBoundingClientRect().width || this.track.clientWidth;
    const a = this.slides[0].getBoundingClientRect();
    const b = this.slides[1].getBoundingClientRect();
    return b.left - a.left;
  }

  _maxScroll() {
    return this.track.scrollWidth - this.track.clientWidth;
  }

  _activeIndex() {
    const stride = this._slideStride() || 1;
    return Math.round(this.track.scrollLeft / stride);
  }

  _goTo(i) {
    const stride = this._slideStride();
    const max = this._maxScroll();
    const left = Math.max(0, Math.min(i * stride, max));
    this.track.scrollTo({ left, behavior: 'smooth' });
  }

  _step(dir) {
    const stride = this._slideStride();
    const max = this._maxScroll();
    let next = this.track.scrollLeft + dir * stride;
    if (dir > 0 && this.track.scrollLeft >= max - 1) next = 0;
    if (dir < 0 && this.track.scrollLeft <= 1) next = max;
    next = Math.max(0, Math.min(next, max));
    this.track.scrollTo({ left: next, behavior: 'smooth' });
  }

  _onScroll() {
    cancelAnimationFrame(this._raf);
    this._raf = requestAnimationFrame(() => this._update());
  }

  _onResize() {
    cancelAnimationFrame(this._raf);
    this._raf = requestAnimationFrame(() => this._update());
  }

  _update() {
    const i = this._activeIndex();
    if (this.dots) {
      this.dots.forEach((d, idx) => {
        const active = idx === i;
        d.classList.toggle('wd-google-reviews__dot--active', active);
        if (active) d.setAttribute('aria-current', 'true');
        else d.removeAttribute('aria-current');
      });
    }
  }

  _userInteract() {
    // Reset autoplay timer when the user steers manually so the next auto
    // advance does not fire immediately after their click.
    if (!this.autoplay) return;
    this._pause();
    this._resume();
  }

  _resume() {
    if (!this.autoplay || this._timer || !this._inView) return;
    this._timer = window.setInterval(() => this._step(1), this.delay);
  }

  _pause() {
    if (this._timer) {
      window.clearInterval(this._timer);
      this._timer = null;
    }
  }

  _observeVisibility() {
    if (typeof IntersectionObserver === 'undefined') {
      this._inView = true;
      return;
    }
    this._io = new IntersectionObserver((entries) => {
      this._inView = entries[0].isIntersecting;
      if (this._inView) this._resume(); else this._pause();
    }, { threshold: 0.2 });
    this._io.observe(this);
  }
}

if (!window.customElements.get('wd-reviews-slider')) {
  window.customElements.define('wd-reviews-slider', WdReviewsSlider);
}

/* ============================================================================
 * WD Description normalizer
 * ----------------------------------------------------------------------------
 * Merchants often paste numbered-step content into the product description
 * as two separate paragraphs (one per number, one per text) instead of a
 * proper <ol>. The browser then renders huge gaps between every line.
 *
 * This runs once on first paint and converts each detected "N. / content"
 * paragraph-pair sequence into a real <ol class="wd-description__numbered">
 * with one <li> per step. Non-matching content is left alone.
 *
 * Side effects: also removes whitespace-only paragraphs that the rich-text
 * editor occasionally inserts between blocks.
 * ========================================================================== */
function wdNormalizeDescriptions() {
  const containers = document.querySelectorAll('.wd-description__body');
  if (!containers.length) return;

  const numRe = /^\s*\d{1,3}\s*[.):]?\s*$/;

  containers.forEach((container) => {
    // Strip stray empty paragraphs first.
    container.querySelectorAll(':scope > p').forEach((p) => {
      if (!(p.textContent || '').trim() && !p.querySelector('img, br + *')) {
        p.remove();
      }
    });

    const children = Array.from(container.children);
    let i = 0;
    while (i < children.length) {
      const el = children[i];
      const isNumberP =
        el && el.tagName === 'P' && numRe.test(el.textContent || '');
      if (!isNumberP) { i++; continue; }

      // Walk forward as long as we keep finding (number-P, content-P) pairs.
      const pairs = [];
      let j = i;
      while (j + 1 < children.length) {
        const numEl = children[j];
        const contentEl = children[j + 1];
        if (!numEl || numEl.tagName !== 'P') break;
        if (!numRe.test(numEl.textContent || '')) break;
        if (!contentEl || contentEl.tagName !== 'P') break;
        if (numRe.test(contentEl.textContent || '')) break;
        pairs.push({ num: numEl, content: contentEl });
        j += 2;
      }

      if (pairs.length >= 2) {
        const ol = document.createElement('ol');
        ol.className = 'wd-description__numbered';
        pairs.forEach(({ content }) => {
          const li = document.createElement('li');
          li.innerHTML = content.innerHTML;
          ol.appendChild(li);
        });
        container.insertBefore(ol, pairs[0].num);
        pairs.forEach(({ num, content }) => {
          num.remove();
          content.remove();
        });
        i = j;
      } else {
        i++;
      }
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', wdNormalizeDescriptions);
} else {
  wdNormalizeDescriptions();
}

/* ============================================================================
 * <wd-kit-customizer> — sections/wd-kit-customizer.liquid
 * ----------------------------------------------------------------------------
 * Live gear-recolour studio. Ported & generalised from the working 2D proof
 * (wd-design-samples/demo-customizer.html).
 *
 *   - Classifies every pixel of a master texture into colour zones by hue,
 *     then repaints each zone to the chosen colour while preserving the
 *     original lightness/shadows → folds & stitching stay photo-real.
 *   - Stamps an optional rider name/number plate on the proof.
 *   - Optional sponsor-logo upload (Google Drive via Apps Script endpoint,
 *     with a design-consult fallback).
 *   - Optional 3D preview (Three.js loaded on demand from a CDN; the live 2D
 *     canvas is fed in as a texture so recolouring re-skins the model).
 *   - Writes the design (colours / name / number / logo + a rendered proof
 *     image) into the product form as line-item properties so production
 *     receives the exact build.
 *
 * All per-product specifics arrive via the JSON config block the section
 * emits — the engine itself is generic (any number of zones).
 * ========================================================================== */
class WdKitCustomizer extends HTMLElement {
  connectedCallback() {
    if (this._init) return;
    this._init = true;

    this.config = this._readConfig();
    if (!this.config || !this.config.image) return;

    this.statusEl = this.querySelector('[data-wd-kc-status]');
    this.canvas = this.querySelector('[data-wd-kc-canvas]');
    if (!this.canvas) { this._setStatus('Preview unavailable'); return; }
    this.ctx = this.canvas.getContext('2d', { willReadFrequently: true });
    this.msEl = this.querySelector('[data-wd-kc-ms]');

    // Engine state
    this.W = 900; this.H = 900;
    this.src = null; this.px = null;
    this.group = null; this.blend = null;
    this.pxH = null; this.pxS = null; this.pxL = null;
    this.zoneRef = [];
    this.SAT_MIN = 0.13; this.L_MIN = 0.10; this.L_MAX = 0.97;
    this._raf = null;
    this._proofTimer = null;

    // Coerce zones to an array — a kit_zones metafield saved as text (not json)
    // arrives here as a string, which would throw on .map and kill the engine.
    let zoneSrc = this.config.zones;
    if (typeof zoneSrc === 'string') { try { zoneSrc = JSON.parse(zoneSrc); } catch (e) { zoneSrc = []; } }
    if (!Array.isArray(zoneSrc)) zoneSrc = [];
    const hexOr = (d, fb) => { d = (d || fb).toUpperCase(); return /^#[0-9A-F]{3,8}$/.test(d) ? d : fb; };
    this.zones = zoneSrc.map((z, i) => ({
      key: z.key || ('zone' + i),
      label: z.label || ('Zone ' + (i + 1)),
      ref: Number(z.ref) || 0,
      tol: Number(z.tol) || 40,
      fade: !!z.fade,                                   // 2-colour gradient zone
      def: hexOr(z.default, '#888888'),
      defB: hexOr(z.defaultB || z.default, '#444444'),  // bottom colour for a fade zone
    }));
    this.colours = this.zones.map((z) => z.def);
    this.coloursB = this.zones.map((z) => (z.fade ? z.defB : null));
    // Skin guard keeps faces/hands in a photo-derived master from recolouring.
    // Auto-disabled when a zone legitimately sits in the skin hue band (e.g. an
    // orange garment) so we don't refuse to recolour a real garment colour.
    this.skinGuard = this.config.skinGuard !== false
      && !this.zones.some((z) => z.ref >= 12 && z.ref <= 50);
    this.palette = this._parsePalette(this.config.paletteRaw);
    this.colourControl = this.config.colourControl === 'swatches' ? 'swatches' : 'dropdown';
    this.plateInk = this._cssVar('--wd-kc-bg', '#101010');
    this.plateText = this._cssVar('--wd-kc-text', '#ffffff');
    this.plateAccent = this._cssVar('--wd-kc-accent', '#b0fc4f');

    this.logos = [];
    this._logoSeq = 0;

    this._buildZoneControls();
    this._promoteCustomizerParts();
    this._bindControls();
    this._bindFormPlumbing();

    this._loadImage();
  }

  disconnectedCallback() {
    this._dead = true;
    if (this._raf) cancelAnimationFrame(this._raf);
    if (this._proofTimer) clearTimeout(this._proofTimer);
    if (this._img) { this._img.onload = null; this._img.onerror = null; this._img = null; }
    this._stop3D();
    // Our property host + proof input live inside the (external, persistent)
    // product form. Remove them so a theme-editor section reload doesn't leave
    // an orphaned host that duplicates line-item properties on add-to-cart.
    if (this._onSubmitPointer) document.removeEventListener('pointerdown', this._onSubmitPointer, true);
    if (this._onSubmitCapture && this.form) this.form.removeEventListener('submit', this._onSubmitCapture, true);
    this._propHost?.remove();
    this._propHost = null;
    this._proofInput = null;
    this._promotedPreview?.remove();
    this._promotedFields?.remove();
    if (this._promotedPreviewTarget && !this._promotedPreviewTarget.children.length) this._promotedPreviewTarget.hidden = true;
    if (this._promotedFieldsTarget && !this._promotedFieldsTarget.children.length) this._promotedFieldsTarget.hidden = true;
  }

  /* ---------------------------------------------------------------- config */
  _readConfig() {
    const node = this.querySelector('.wd-kc__config');
    if (!node) return null;
    try { return JSON.parse(node.textContent); } catch (e) { console.warn('[wd-kit-customizer] bad config', e); return null; }
  }

  // Each line is "NAME #HEX [NUMBER]" — the number (brand swatch code) is
  // optional, shown to the customer and saved on the order. Returns
  // [name, hex, code] tuples.
  _parsePalette(raw) {
    if (!raw) return [];
    return raw.split('\n').map((line) => {
      const t = line.trim();
      if (!t) return null;
      const m = t.match(/#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})\b/);
      if (!m) return null;
      const hex = '#' + m[1];
      const name = t.slice(0, m.index).replace(/[|:,\-–—]+\s*$/, '').trim() || hex;
      const code = t.slice(m.index + m[0].length).replace(/^[|:,\-–—\s]+/, '').trim();
      return [name, hex.toUpperCase(), code];
    }).filter(Boolean);
  }

  _cssVar(name, fallback) {
    const v = getComputedStyle(this).getPropertyValue(name).trim();
    return v || fallback;
  }

  /* --------------------------------------------------------- colour utils */
  _hex2rgb(h) { h = h.replace('#', ''); if (h.length === 3) h = h.split('').map((c) => c + c).join(''); return [parseInt(h.substr(0, 2), 16), parseInt(h.substr(2, 2), 16), parseInt(h.substr(4, 2), 16)]; }
  _rgb2hsl(r, g, b) { r /= 255; g /= 255; b /= 255; const mx = Math.max(r, g, b), mn = Math.min(r, g, b); let h = 0, s = 0; const l = (mx + mn) / 2; if (mx !== mn) { const d = mx - mn; s = l > 0.5 ? d / (2 - mx - mn) : d / (mx + mn); switch (mx) { case r: h = (g - b) / d + (g < b ? 6 : 0); break; case g: h = (b - r) / d + 2; break; default: h = (r - g) / d + 4; } h *= 60; } return [h, s, l]; }
  _hsl2rgb(h, s, l) { h = ((h % 360) + 360) % 360; const c = (1 - Math.abs(2 * l - 1)) * s, x = c * (1 - Math.abs((h / 60) % 2 - 1)), m = l - c / 2; let r = 0, g = 0, b = 0; if (h < 60) { r = c; g = x; } else if (h < 120) { r = x; g = c; } else if (h < 180) { g = c; b = x; } else if (h < 240) { g = x; b = c; } else if (h < 300) { r = x; b = c; } else { r = c; b = x; } return [(r + m) * 255, (g + m) * 255, (b + m) * 255]; }
  _hueDist(a, b) { let d = Math.abs(a - b) % 360; return d > 180 ? 360 - d : d; }
  _lerpHue(a, b, t) { const d = ((b - a + 540) % 360) - 180; return ((a + d * t) % 360 + 360) % 360; }

  /* -------------------------------------------------------------- engine */
  _loadImage() {
    const img = this._img = new Image();
    img.crossOrigin = 'anonymous'; // master may be served from the Shopify CDN
    img.onload = () => {
      if (this._dead || !this.isConnected) return; // element torn down mid-load
      this.W = img.naturalWidth || 900; this.H = img.naturalHeight || 900;
      this.canvas.width = this.W; this.canvas.height = this.H;
      this.ctx.drawImage(img, 0, 0, this.W, this.H);
      try {
        this.src = this.ctx.getImageData(0, 0, this.W, this.H);
      } catch (e) {
        // Tainted canvas (cross-origin without CORS) — preview still shows, recolour disabled.
        this._setStatus('Preview only — recolour unavailable', false, true);
        return;
      }
      this.px = this.src.data;
      this._classify();
      (document.fonts ? document.fonts.ready : Promise.resolve()).then(() => this._recolor());
      this._setStatus('Engine ready — ' + (this.W * this.H).toLocaleString() + ' pixels mapped', true);
      this._syncProperties();
    };
    img.onerror = () => { if (this._dead) return; this._setStatus('Could not load the design texture', false, true); };
    img.src = this.config.image;
  }

  _classify() {
    const n = this.W * this.H, Z = this.zones;
    this.group = new Int8Array(n).fill(-1);
    this.blend = new Float32Array(n);
    this.pxH = new Float32Array(n); this.pxS = new Float32Array(n); this.pxL = new Float32Array(n);
    const acc = Z.map(() => ({ h: 0, s: 0, l: 0, n: 0 }));
    this.zoneYMin = new Float32Array(Z.length).fill(Infinity);
    this.zoneYMax = new Float32Array(Z.length).fill(-Infinity);
    const W = this.W, px = this.px;
    for (let i = 0; i < n; i++) {
      const j = i * 4;
      const hsl = this._rgb2hsl(px[j], px[j + 1], px[j + 2]);
      const h = hsl[0], s = hsl[1], l = hsl[2];
      this.pxH[i] = h; this.pxS[i] = s; this.pxL[i] = l;
      if (s < this.SAT_MIN || l < this.L_MIN || l > this.L_MAX) continue;
      // Leave skin-toned pixels (faces/hands) unclassified so they keep their
      // original colour instead of recolouring with the garment. Two bands: a
      // "core" tan/orange skin hue (clearly above a coral garment) is protected
      // even when fairly saturated; a "soft" band nearer coral is protected only
      // at low saturation, so a vivid garment colour there still recolours.
      if (this.skinGuard && l >= 0.25 && l <= 0.93) {
        const skinCore = h >= 22 && h <= 50 && s >= 0.12 && s <= 0.78;
        const skinSoft = h >= 14 && h < 22 && s >= 0.15 && s <= 0.50;
        if (skinCore || skinSoft) continue;
      }
      let best = -1, bestD = 1e9;
      for (let z = 0; z < Z.length; z++) {
        const d = this._hueDist(h, Z[z].ref);
        if (d < Z[z].tol && d < bestD) { bestD = d; best = z; }
      }
      if (best < 0) continue;
      this.group[i] = best;
      const yy = (i / W) | 0;
      if (yy < this.zoneYMin[best]) this.zoneYMin[best] = yy;
      if (yy > this.zoneYMax[best]) this.zoneYMax[best] = yy;
      const fSat = Math.min(1, (s - this.SAT_MIN) / 0.07);
      const fHue = Math.min(1, (Z[best].tol - bestD) / 12);
      this.blend[i] = Math.max(0, Math.min(1, Math.min(fSat, fHue)));
      const a = acc[best]; a.h += h; a.s += s; a.l += l; a.n++;
    }
    this.zoneRef = acc.map((a) => a.n ? { h: a.h / a.n, s: a.s / a.n, l: a.l / a.n } : { h: 0, s: 0.5, l: 0.5 });
  }

  _scheduleRecolor() { if (this._raf) cancelAnimationFrame(this._raf); this._raf = requestAnimationFrame(() => this._recolor()); }

  _recolor() {
    if (this._dead || !this.src) return;
    const t0 = performance.now();
    // Reuse one output buffer; every pixel's 4 channels are rewritten each pass,
    // so no clearing is needed. Avoids ~3MB GC churn per recolour (picker drag).
    if (!this._out || this._out.width !== this.W || this._out.height !== this.H) {
      this._out = this.ctx.createImageData(this.W, this.H);
    }
    const out = this._out;
    const o = out.data, px = this.px, n = this.W * this.H, W = this.W;
    const toHSL = (hex) => { const c = this._hex2rgb(hex); return this._rgb2hsl(c[0], c[1], c[2]); };
    const targets = this.zones.map((z, gi) => {
      if (z.fade) {
        const A = toHSL(this.colours[gi]), B = toHSL(this.coloursB[gi]);
        return { fade: true, hA: A[0], sA: A[1], lA: A[2], hB: B[0], sB: B[1], lB: B[2], y0: this.zoneYMin[gi], span: (this.zoneYMax[gi] - this.zoneYMin[gi]) || 1 };
      }
      const H = toHSL(this.colours[gi]);
      return { h: H[0], s: H[1], l: H[2] };
    });
    for (let i = 0; i < n; i++) {
      const j = i * 4, g = this.group[i];
      if (g < 0 || this.blend[i] === 0 || !targets[g]) { o[j] = px[j]; o[j + 1] = px[j + 1]; o[j + 2] = px[j + 2]; o[j + 3] = px[j + 3]; continue; }
      const t = targets[g], ref = this.zoneRef[g];
      let th, ts, tl;
      if (t.fade) {
        const tt = Math.max(0, Math.min(1, (((i / W) | 0) - t.y0) / t.span));
        th = this._lerpHue(t.hA, t.hB, tt);
        ts = t.sA + (t.sB - t.sA) * tt;
        tl = t.lA + (t.lB - t.lA) * tt;
      } else { th = t.h; ts = t.s; tl = t.l; }
      const s = Math.max(0, Math.min(1, this.pxS[i] * (ts / (ref.s || 0.5))));
      const l = Math.max(0.02, Math.min(0.98, tl < ref.l ? this.pxL[i] * (tl / (ref.l || 0.5)) : this.pxL[i] + (tl - ref.l) * 0.82));
      const rgb = this._hsl2rgb(th, s, l);
      const k = this.blend[i], ik = 1 - k;
      o[j] = rgb[0] * k + px[j] * ik;
      o[j + 1] = rgb[1] * k + px[j + 1] * ik;
      o[j + 2] = rgb[2] * k + px[j + 2] * ik;
      o[j + 3] = px[j + 3];
    }
    this.ctx.putImageData(out, 0, 0);
    this._drawRiderPlate();
    if (this.msEl) this.msEl.textContent = Math.round(performance.now() - t0) + 'ms';
    if (this._tex) this._tex.needsUpdate = true;
    this._scheduleProof();
  }

  _drawRiderPlate() {
    const nn = this.config.nameNumber || {};
    if (!nn.enabled) return;
    let name = (this.nameInput?.value || '').trim();
    const num = (this.numInput?.value || '').trim();
    if (nn.uppercase) name = name.toUpperCase();
    if (!name && !num) return;
    const s = this.W / 900, ctx = this.ctx;
    ctx.save();
    const pw = 250 * s, ph = 86 * s, x = this.W - pw - 28 * s, y = 28 * s;
    ctx.fillStyle = this.plateInk; ctx.fillRect(x, y, pw, ph);
    ctx.fillStyle = this.plateAccent; ctx.fillRect(x, y, 6 * s, ph);
    ctx.fillStyle = this.plateText; ctx.globalAlpha = 0.6;
    ctx.font = 700 + ' ' + (11 * s) + 'px JetBrains Mono, ui-monospace, monospace';
    ctx.textBaseline = 'top';
    ctx.fillText('RIDER ID — PRINT PROOF', x + 18 * s, y + 12 * s);
    ctx.globalAlpha = 1;
    ctx.fillStyle = this.plateText; ctx.font = '900 ' + (26 * s) + 'px Archivo, system-ui, sans-serif';
    ctx.fillText((name || '—'), x + 18 * s, y + 36 * s);
    if (num) {
      const nw = ctx.measureText(name ? name + '  ' : '').width;
      ctx.fillStyle = this.plateAccent;
      ctx.fillText('#' + num, x + 18 * s + nw, y + 36 * s);
    }
    ctx.restore();
  }

  /* ------------------------------------------------------------- controls */
  _buildZoneControls() {
    const host = this.querySelector('[data-wd-kc-zones]');
    if (!host) return;
    this.zones.forEach((zone, slot) => {
      if (zone.fade) {
        this._buildSwatchControl(host, zone, slot, 'a');
        this._buildSwatchControl(host, zone, slot, 'b');
      } else {
        this._buildSwatchControl(host, zone, slot, null);
      }
    });
  }

  _promoteCustomizerParts() {
    const productId = String(this.config.productId || '');
    const findMount = (selector, dataKey) => {
      const targets = Array.from(document.querySelectorAll(selector));
      return targets.find((el) => el.dataset[dataKey] === productId) || targets[0];
    };

    const preview = this.querySelector('.wd-kc__preview');
    const previewTarget = findMount('[data-wd-kc-preview-mount]', 'wdKcPreviewMount');
    if (preview && previewTarget) {
      this._copyThemeVars(preview);
      previewTarget.hidden = false;
      previewTarget.appendChild(preview);
      this._promotedPreview = preview;
      this._promotedPreviewTarget = previewTarget;
    }

    const fields = this.querySelector('.wd-kc__config-panel');
    const fieldsTarget = findMount('[data-wd-kc-fields]', 'wdKcFields');
    if (fields && fieldsTarget) {
      this._copyThemeVars(fields);
      fieldsTarget.hidden = false;
      fields.classList.add('wd-kc__config-panel--promoted');
      fieldsTarget.appendChild(fields);
      this._promotedFields = fields;
      this._promotedFieldsTarget = fieldsTarget;
    }

    if (this._promotedPreview || this._promotedFields) this.classList.add('wd-kc--mounted');
  }

  _copyThemeVars(target) {
    const styles = getComputedStyle(this);
    [
      '--wd-kc-bg',
      '--wd-kc-panel',
      '--wd-kc-line',
      '--wd-kc-text',
      '--wd-kc-muted',
      '--wd-kc-accent',
      '--wd-kc-on-accent',
      '--wd-kc-canvas-bg',
    ].forEach((name) => {
      const value = styles.getPropertyValue(name).trim();
      if (value) target.style.setProperty(name, value);
    });
  }

  _query(selector) {
    return this.querySelector(selector)
      || this._promotedPreview?.querySelector(selector)
      || this._promotedFields?.querySelector(selector);
  }

  _queryAll(selector) {
    return [
      ...this.querySelectorAll(selector),
      ...(this._promotedPreview ? this._promotedPreview.querySelectorAll(selector) : []),
      ...(this._promotedFields ? this._promotedFields.querySelectorAll(selector) : []),
    ];
  }

  // Builds one colour control group. `end` is null (normal zone), 'a' (fade
  // top) or 'b' (fade bottom). Renders a named dropdown (reference parity) or
  // a swatch grid depending on the section's Colour control setting.
  _buildSwatchControl(host, zone, slot, end) {
    const cid = end ? slot + '.' + end : String(slot);
    const cur = (end === 'b' ? this.coloursB[slot] : this.colours[slot]).toUpperCase();
    const title = end ? zone.label + ' — ' + (end === 'a' ? 'Top' : 'Bottom') : zone.label;
    const group = document.createElement('div');
    group.className = 'wd-kc__group';
    group.innerHTML =
      '<div class="wd-kc__group-head">' +
        '<span class="wd-kc__group-title">' + this._esc(title) + '</span>' +
        '<span class="wd-kc__group-val" data-wd-kc-zoneval="' + cid + '">' + this._esc(this._colourValLabel(cur)) + '</span>' +
      '</div>';
    host.appendChild(group);

    if (this.colourControl === 'swatches') this._buildSwatches(group, cid, slot, title, cur, end);
    else this._buildDropdown(group, cid, slot, title, cur, end);
  }

  _buildSwatches(group, cid, slot, title, cur, end) {
    const wrap = document.createElement('div');
    wrap.className = 'wd-kc__swatches';
    wrap.setAttribute('data-wd-kc-swatches', cid);
    group.appendChild(wrap);
    this.palette.forEach((entry) => {
      const name = entry[0], hex = entry[1], code = entry[2];
      const b = document.createElement('button');
      b.type = 'button';
      const isSel = hex === cur;
      b.className = 'wd-kc__sw' + (isSel ? ' is-active' : '');
      b.style.background = hex; b.title = code ? name + ' · ' + code : name; b.dataset.hex = hex;
      b.setAttribute('aria-label', title + ': ' + b.title);
      b.setAttribute('aria-pressed', String(isSel));
      b.addEventListener('click', () => this._setColour(slot, hex, end));
      wrap.appendChild(b);
    });
    if (this.config.allowCustomColor) {
      const label = document.createElement('label');
      label.className = 'wd-kc__sw-custom'; label.title = 'Custom colour';
      label.innerHTML = '<span aria-hidden="true">+</span>';
      const inp = document.createElement('input');
      inp.type = 'color'; inp.value = cur;
      inp.setAttribute('aria-label', title + ': custom colour');
      inp.addEventListener('input', (e) => this._setColour(slot, e.target.value, end));
      label.appendChild(inp); wrap.appendChild(label);
    }
  }

  _buildDropdown(group, cid, slot, title, cur, end) {
    const row = document.createElement('div');
    row.className = 'wd-kc__colourselect';

    const chip = document.createElement('span');
    chip.className = 'wd-kc__cs-chip';
    chip.setAttribute('data-wd-kc-chip', cid);
    chip.setAttribute('aria-hidden', 'true');
    chip.style.background = cur;
    row.appendChild(chip);

    const sel = document.createElement('select');
    sel.className = 'wd-kc__select';
    sel.setAttribute('data-wd-kc-select', cid);
    sel.setAttribute('aria-label', title);
    this.palette.forEach((entry) => {
      const o = document.createElement('option');
      o.value = entry[1];
      o.textContent = entry[2] ? entry[0] + ' — ' + entry[2] : entry[0];
      if (entry[1] === cur) o.selected = true;
      sel.appendChild(o);
    });

    let customInput = null;
    if (this.config.allowCustomColor) {
      const co = document.createElement('option');
      co.value = '__custom'; co.textContent = 'Custom colour…';
      sel.appendChild(co);
    }
    sel.addEventListener('change', () => {
      if (sel.value === '__custom') { if (customInput) customInput.click(); return; }
      this._setColour(slot, sel.value, end);
    });
    row.appendChild(sel);

    if (this.config.allowCustomColor) {
      const label = document.createElement('label');
      label.className = 'wd-kc__sw-custom'; label.title = 'Custom colour';
      label.innerHTML = '<span aria-hidden="true">+</span>';
      customInput = document.createElement('input');
      customInput.type = 'color'; customInput.value = cur;
      customInput.setAttribute('aria-label', title + ': custom colour');
      customInput.addEventListener('input', (e) => this._setColour(slot, e.target.value, end));
      label.appendChild(customInput); row.appendChild(label);
    }

    group.appendChild(row);

    // A starting colour that isn't in the palette shows as the Custom option.
    if (!this.palette.some((e) => e[1] === cur)) {
      const c = sel.querySelector('option[value="__custom"]');
      if (c) { c.textContent = 'Custom — ' + cur; sel.value = '__custom'; }
    }
  }

  // The short label shown beside a zone title (name · number, or the hex).
  _colourValLabel(hex) {
    hex = String(hex).toUpperCase();
    const m = this.palette.find((e) => e[1] === hex);
    if (!m) return hex;
    return m[2] ? m[0] + ' · ' + m[2] : m[0];
  }

  // The colour value saved on the order: "NAME (NUMBER) — #HEX" so production
  // sees the brand swatch name + number, falling back to the raw hex.
  _colourLabel(hex) {
    if (hex == null || hex === '') return hex;
    hex = String(hex).toUpperCase();
    const m = this.palette.find((e) => e[1] === hex);
    if (!m) return hex;
    return m[0] + (m[2] ? ' (' + m[2] + ')' : '') + ' — ' + hex;
  }

  // Keeps a zone's controls (chip, dropdown, swatch states, value label) in
  // sync with its current colour, whichever control type is in use.
  _refreshColourUI(cid, hex) {
    hex = String(hex).toUpperCase();
    const val = this._query('[data-wd-kc-zoneval="' + cid + '"]');
    if (val) val.textContent = this._colourValLabel(hex);
    const chip = this._query('[data-wd-kc-chip="' + cid + '"]');
    if (chip) chip.style.background = hex;
    const sel = this._query('[data-wd-kc-select="' + cid + '"]');
    if (sel) {
      const opt = Array.from(sel.options).find((o) => o.value.toUpperCase() === hex);
      if (opt) { sel.value = opt.value; }
      else {
        const c = sel.querySelector('option[value="__custom"]');
        if (c) { c.textContent = 'Custom — ' + hex; sel.value = '__custom'; }
      }
    }
    const sw = this._query('[data-wd-kc-swatches="' + cid + '"]');
    if (sw) sw.querySelectorAll('.wd-kc__sw').forEach((s) => {
      const a = s.dataset.hex === hex;
      s.classList.toggle('is-active', a);
      s.setAttribute('aria-pressed', String(a));
    });
  }

  _setColour(slot, hex, end) {
    hex = hex.toUpperCase();
    if (end === 'b') this.coloursB[slot] = hex; else this.colours[slot] = hex;
    const cid = end ? slot + '.' + end : String(slot);
    this._refreshColourUI(cid, hex);
    this._scheduleRecolor();
    this._syncProperties();
  }

  _bindControls() {
    const nn = this.config.nameNumber || {};
    if (nn.enabled) {
      this.nameInput = this._query('[data-wd-kc-name]');
      this.numInput = this._query('[data-wd-kc-number]');
      this.nameInput?.addEventListener('input', () => { this._scheduleRecolor(); this._syncProperties(); });
      this.numInput?.addEventListener('input', (e) => {
        e.target.value = e.target.value.replace(/[^0-9A-Za-z]/g, '');
        this._scheduleRecolor(); this._syncProperties();
      });
    }

    this._query('[data-wd-kc-reset]')?.addEventListener('click', () => this._reset());
    this._query('[data-wd-kc-download]')?.addEventListener('click', () => this._download());

    // View tabs (2D / 3D)
    this._queryAll('[data-wd-kc-view]').forEach((tab) => {
      tab.addEventListener('click', () => this._setView(tab.dataset.wdKcView));
    });

    if (this.config.logo && this.config.logo.enabled) this._bindLogo();
  }

  _reset() {
    this.colours = this.zones.map((z) => z.def);
    this.coloursB = this.zones.map((z) => (z.fade ? z.defB : null));
    this.zones.forEach((z, slot) => {
      if (z.fade) { this._refreshColourUI(slot + '.a', this.colours[slot]); this._refreshColourUI(slot + '.b', this.coloursB[slot]); }
      else this._refreshColourUI(String(slot), this.colours[slot]);
    });
    if (this.nameInput) this.nameInput.value = '';
    if (this.numInput) this.numInput.value = '';
    this._scheduleRecolor();
    this._syncProperties();
    this._setStatus('Design reset to defaults');
  }

  _download() {
    const a = document.createElement('a');
    a.download = 'mvd-kit-proof-' + this.colours.join('-').replace(/#/g, '') + '.jpg';
    a.href = this.canvas.toDataURL('image/jpeg', 0.92);
    a.click();
    this._setStatus('Proof image downloaded');
  }

  _setStatus(text, ready, urgent) {
    if (!this.statusEl) return;
    this.statusEl.textContent = text;
    this.statusEl.classList.toggle('is-ready', !!ready);
    this.statusEl.setAttribute('aria-live', urgent ? 'assertive' : 'polite');
  }

  // Escapes for both text and attribute contexts (quotes included), so it is
  // safe to interpolate user-controlled values (e.g. uploaded filenames) into
  // innerHTML attribute values.
  _esc(s) {
    return (s == null ? '' : String(s))
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }
  _attr(s) { return this._esc(s); }

  /* ----------------------------------------------------------- logo upload */
  // The kit has a fixed set of placements (default 24). Each can carry one
  // logo, so production knows which file goes on which spot. Names are optional
  // (one per line in the section); without them slots read "Position N".
  _buildLogoPositions() {
    const cfg = this.config.logo || {};
    const count = Math.max(1, Math.min(99, parseInt(cfg.count, 10) || 24));
    const names = (cfg.namesRaw || '').split('\n').map((s) => s.trim());
    this.logoPositions = [];
    for (let i = 1; i <= count; i++) this.logoPositions.push({ num: i, name: names[i - 1] || '' });
  }

  _pad2(n) { return String(n).length < 2 ? '0' + n : String(n); }

  // Display label for a placement: "3 — Right chest" when named, else "Position 3".
  _positionLabel(num) {
    const p = this.logoPositions.find((x) => x.num === num);
    return p && p.name ? num + ' — ' + p.name : 'Position ' + num;
  }

  // Order property key for a placement, e.g. "Logo 03" or "Logo 03 (Right chest)".
  _logoPropLabel(num) {
    const p = this.logoPositions.find((x) => x.num === num);
    return 'Logo ' + this._pad2(num) + (p && p.name ? ' (' + p.name + ')' : '');
  }

  _bindLogo() {
    this._buildLogoPositions();
    this.logoInput = this._query('[data-wd-kc-logo]');
    this.logoList = this._query('[data-wd-kc-logo-list]');
    this.logoStatus = this._query('[data-wd-kc-logo-status]');
    this.logoPosSelect = this._query('[data-wd-kc-logo-pos]');
    this.logoCountEl = this._query('[data-wd-kc-logo-count]');
    const drop = this._query('[data-wd-kc-logo-drop]');
    if (!this.logoInput || !this.logoPosSelect) return;

    this._renderPositionOptions();

    this.logoInput.addEventListener('change', (e) => this._addLogoForPosition(e.target.files));
    if (drop) {
      ['dragenter', 'dragover'].forEach((ev) => drop.addEventListener(ev, (e) => { e.preventDefault(); drop.classList.add('is-drag'); }));
      ['dragleave', 'drop'].forEach((ev) => drop.addEventListener(ev, (e) => { e.preventDefault(); drop.classList.remove('is-drag'); }));
      drop.addEventListener('drop', (e) => { if (e.dataTransfer?.files?.length) this._addLogoForPosition(e.dataTransfer.files); });
    }
  }

  // Rebuilds the placement dropdown, marking filled slots and selecting the
  // first still-empty placement so repeated uploads flow down the list.
  _renderPositionOptions() {
    if (!this.logoPosSelect) return;
    this.logoPosSelect.innerHTML = '';
    this.logoPositions.forEach((p) => {
      const o = document.createElement('option');
      o.value = String(p.num);
      const used = this.logos.some((l) => l.position === p.num);
      o.textContent = this._positionLabel(p.num) + (used ? ' ✓' : '');
      this.logoPosSelect.appendChild(o);
    });
    const firstFree = this.logoPositions.find((p) => !this.logos.some((l) => l.position === p.num));
    this.logoPosSelect.value = String((firstFree || this.logoPositions[this.logoPositions.length - 1]).num);
  }

  _addLogoForPosition(fileList) {
    const files = Array.from(fileList || []);
    if (!files.length || !this.logoPosSelect) return;
    const pos = parseInt(this.logoPosSelect.value, 10) || this.logoPositions[0].num;
    const file = files[0]; // one logo per placement
    const cfg = this.config.logo, maxBytes = (cfg.maxMB || 25) * 1024 * 1024;
    // base64 + JSON inflate the payload ~37%; keep the on-the-wire size under the
    // Apps Script ~50MB ceiling so a doomed upload is flagged up front, not mid-fetch.
    const wireLimit = 50 * 1024 * 1024;

    this.logos = this.logos.filter((l) => l.position !== pos); // replace any existing logo for this placement
    const entry = { id: ++this._logoSeq, position: pos, file, name: file.name, size: file.size, status: 'pending', link: '' };
    if (file.size > maxBytes || (cfg.endpoint && file.size * 1.37 > wireLimit)) entry.status = 'toolarge';
    this.logos.push(entry);
    this.logos.sort((a, b) => a.position - b.position);

    this._renderLogoList();
    this._renderPositionOptions();
    if (this.logoInput) this.logoInput.value = ''; // allow re-selecting the same file

    if (entry.status === 'toolarge') { this._syncProperties(); return; }
    if (cfg.endpoint) this._uploadLogo(entry); else { entry.status = 'consult'; this._renderLogoList(); this._syncProperties(); }
  }

  async _uploadLogo(entry) {
    entry.status = 'uploading'; this._renderLogoList();
    try {
      const dataUrl = await this._fileToDataURL(entry.file);
      const base64 = dataUrl.split(',')[1];
      const res = await fetch(this.config.logo.endpoint, {
        method: 'POST',
        // text/plain avoids a CORS preflight against the Apps Script endpoint
        headers: { 'Content-Type': 'text/plain;charset=utf-8' },
        body: JSON.stringify({ token: this.config.logo.token || '', filename: entry.name, mimeType: entry.file.type || 'application/octet-stream', data: base64, product: this.config.designName }),
      });
      const json = await res.json();
      if (json && (json.url || json.link)) {
        entry.status = 'done'; entry.link = json.url || json.link;
      } else {
        console.warn('[wd-kit] logo upload: unexpected response, falling back to consult', json);
        entry.status = 'consult';
      }
    } catch (e) {
      // A blanket "always consult" usually means the cross-origin JSON read of the
      // Apps Script response broke — surface it instead of silently swallowing.
      console.warn('[wd-kit] logo upload failed (network or cross-origin read), falling back to consult', e);
      entry.status = 'consult';
    }
    this._renderLogoList();
    this._syncProperties();
  }

  _fileToDataURL(file) {
    return new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.onerror = reject;
      r.readAsDataURL(file);
    });
  }

  _renderLogoList() {
    if (this.logoCountEl) this.logoCountEl.textContent = this.logos.length + ' / ' + (this.logoPositions ? this.logoPositions.length : 0);
    if (!this.logoList) return;
    this.logoList.innerHTML = '';
    const labels = { pending: 'Queued', uploading: 'Uploading…', done: 'Uploaded', consult: 'Sent with consult', toolarge: 'Too large — design consult' };
    this.logos.forEach((entry) => {
      const li = document.createElement('li');
      li.className = 'wd-kc__logo-item is-' + entry.status;
      li.innerHTML =
        '<span class="wd-kc__logo-pos">' + this._esc(this._positionLabel(entry.position)) + '</span>' +
        '<span class="wd-kc__logo-name">' + this._esc(entry.name) + '</span>' +
        '<span class="wd-kc__logo-meta">' + this._formatSize(entry.size) + ' · ' + labels[entry.status] + '</span>' +
        '<button type="button" class="wd-kc__logo-remove" aria-label="Remove logo on ' + this._attr(this._positionLabel(entry.position)) + '">×</button>';
      li.querySelector('.wd-kc__logo-remove').addEventListener('click', () => {
        this.logos = this.logos.filter((l) => l.id !== entry.id);
        this._renderLogoList(); this._renderPositionOptions(); this._syncProperties();
      });
      this.logoList.appendChild(li);
    });
    if (this.logoStatus) {
      const cfg = this.config.logo;
      const needsConsult = this.logos.some((l) => l.status === 'consult' || l.status === 'toolarge');
      this.logoStatus.textContent = needsConsult && cfg.consultFallback ? cfg.consultMessage : '';
    }
  }

  _formatSize(bytes) {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + ' KB';
    return (bytes / 1024 / 1024).toFixed(1) + ' MB';
  }

  /* ----------------------------------------------------- cart / properties */
  // Find THE main product form (not a Complete-Your-Kit / recommendation
  // product-card form that may live on the same page).
  _findForm() {
    const pid = this.config.productId;
    return (pid && document.querySelector('.shopify-section--main-product form[is="product-form"][id^="product-form-' + pid + '-"]'))
      || document.querySelector('.shopify-section--main-product form[is="product-form"]')
      || document.querySelector('form[is="product-form"]');
  }

  _bindFormPlumbing() {
    this.form = this._findForm();
    if (!this.form) return;

    // Customized items must go through the standard AJAX add-to-cart so the proof
    // file + line-item properties are transmitted. Shop Pay / dynamic checkout
    // cannot carry an uploaded file and may drop injected properties — hide it.
    this.form.querySelectorAll('.shopify-payment-button').forEach((el) => el.style.setProperty('display', 'none', 'important'));

    // Drop any orphaned host from a previous init (theme-editor reload).
    this.form.querySelector(':scope > [data-wd-kc-props]')?.remove();

    this._propHost = document.createElement('div');
    this._propHost.hidden = true;
    this._propHost.setAttribute('data-wd-kc-props', '');
    this.form.appendChild(this._propHost);

    // File input for the rendered proof image (attaches the visual to the order).
    this._proofInput = document.createElement('input');
    this._proofInput.type = 'file';
    this._proofInput.name = this._propName('Design proof');
    this._proofInput.hidden = true;
    this._proofInput.tabIndex = -1;
    this._propHost.appendChild(this._proofInput);

    // Regenerate the proof synchronously the moment ANY add-to-cart button for
    // this form is pressed. The sticky "add to cart on scroll" and quick-buy
    // buttons live OUTSIDE the form (linked via the HTML form="" attribute), so
    // a form-scoped query would miss them — use a capture-phase document listener
    // filtered by native form association, which also covers late-rendered buttons.
    this._onSubmitPointer = (e) => {
      const btn = e.target.closest && e.target.closest('button, input[type="submit"]');
      if (btn && this.form && btn.form === this.form) this._updateProofFile(true);
    };
    document.addEventListener('pointerdown', this._onSubmitPointer, true);
    // Backstop for keyboard/Enter and Buy-now submits that skip pointerdown.
    this._onSubmitCapture = () => this._updateProofFile(true);
    this.form.addEventListener('submit', this._onSubmitCapture, true);
  }

  // Defensive: this theme updates variants in place (it does not replace the
  // form), but if any flow ever re-renders it, re-find the form and re-attach
  // our hidden property host + proof input so the design still reaches the cart.
  _ensureHost() {
    if (!this._propHost) return false;
    const live = this._findForm();
    if (live && live !== this.form) {
      if (this._onSubmitCapture && this.form) this.form.removeEventListener('submit', this._onSubmitCapture, true);
      this.form = live;
      this.form.querySelectorAll('.shopify-payment-button').forEach((el) => el.style.setProperty('display', 'none', 'important'));
      if (this._onSubmitCapture) this.form.addEventListener('submit', this._onSubmitCapture, true);
    }
    if (this.form && !this._propHost.isConnected) this.form.appendChild(this._propHost);
    if (this._proofInput && !this._proofInput.isConnected) this._propHost.appendChild(this._proofInput);
    return true;
  }

  _propName(label) {
    const prefix = (this.config.propertyPrefix || '').trim();
    return 'properties[' + (prefix ? prefix + ' ' : '') + label + ']';
  }

  _setProp(label, value) {
    if (!this._propHost) return;
    const name = this._propName(label);
    let input = this._propHost.querySelector('input[data-label="' + CSS.escape(label) + '"]');
    if (value == null || value === '') { if (input) input.remove(); return; }
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.dataset.label = label;
      this._propHost.appendChild(input);
    }
    input.name = name;
    input.value = value;
  }

  _syncProperties() {
    this._updateCanvasLabel();
    if (!this._ensureHost()) return;
    this._setProp('Design', this.config.designName);
    this.zones.forEach((z, i) => {
      if (z.fade) {
        this._setProp('Colour — ' + z.label + ' (top)', this._colourLabel(this.colours[i]));
        this._setProp('Colour — ' + z.label + ' (bottom)', this._colourLabel(this.coloursB[i]));
      } else {
        this._setProp('Colour — ' + z.label, this._colourLabel(this.colours[i]));
      }
    });

    const nn = this.config.nameNumber || {};
    if (nn.enabled) {
      let name = (this.nameInput?.value || '').trim();
      if (nn.uppercase) name = name.toUpperCase();
      this._setProp(nn.nameLabel || 'Rider name', name);
      this._setProp(nn.numberLabel || 'Number', (this.numInput?.value || '').trim());
    }

    if (this.config.logo && this.config.logo.enabled) {
      this._syncLogoFileProperties();
      // One property per placement so production sees logo-for-position. Empty
      // placements clear themselves (_setProp removes the input on a blank value).
      (this.logoPositions || []).forEach((p) => {
        const entry = this.logos.find((l) => l.position === p.num);
        let val = '';
        if (entry) {
          if (entry.status === 'done') val = entry.name + ' — ' + entry.link;
          else if (entry.status === 'toolarge' || entry.status === 'consult') val = entry.name + ' — to follow (design consult)';
          else val = entry.name + ' — uploading…';
        }
        this._setProp(this._logoPropLabel(p.num), val);
      });
    }
  }

  _syncLogoFileProperties() {
    if (!this._propHost) return;
    this._propHost.querySelectorAll('[data-wd-kc-logo-file]').forEach((input) => input.remove());

    this.logos.forEach((entry) => {
      if (!entry.file || entry.status === 'toolarge') return;
      try {
        const input = document.createElement('input');
        const dt = new DataTransfer();
        dt.items.add(entry.file);
        input.type = 'file';
        input.name = this._propName('Logo file ' + this._pad2(entry.position));
        input.hidden = true;
        input.tabIndex = -1;
        input.dataset.wdKcLogoFile = String(entry.id);
        input.files = dt.files;
        this._propHost.appendChild(input);
      } catch (e) {
        // If a browser blocks programmatic file inputs, keep the text/link
        // properties and consult fallback rather than breaking add-to-cart.
      }
    });
  }

  // Keep the canvas accessible name describing the live design (colours + name/number).
  _updateCanvasLabel() {
    if (!this.canvas) return;
    const colourName = (hex) => {
      const m = this.palette.find((e) => e[1] === String(hex).toUpperCase());
      return m ? m[0] : hex;
    };
    const parts = this.zones.map((z, i) => z.fade
      ? z.label + ' ' + colourName(this.colours[i]) + ' to ' + colourName(this.coloursB[i])
      : z.label + ' ' + colourName(this.colours[i]));
    let label = (this.config.designName || 'Gear') + ' preview — ' + parts.join(', ');
    const nn = this.config.nameNumber || {};
    if (nn.enabled) {
      let nm = (this.nameInput?.value || '').trim();
      if (nn.uppercase) nm = nm.toUpperCase();
      const num = (this.numInput?.value || '').trim();
      if (nm) label += '; ' + (nn.nameLabel || 'name') + ' ' + nm;
      if (num) label += '; ' + (nn.numberLabel || 'number') + ' ' + num;
    }
    this.canvas.setAttribute('aria-label', label);
  }

  _scheduleProof() {
    if (this._proofTimer) clearTimeout(this._proofTimer);
    this._proofTimer = setTimeout(() => this._updateProofFile(false), 350);
  }

  _updateProofFile(sync) {
    if (!this.src) return;
    this._ensureHost();
    if (!this._proofInput) return;
    const make = (blob) => {
      if (!blob) return;
      try {
        const file = new File([blob], 'kit-design-proof.jpg', { type: 'image/jpeg' });
        const dt = new DataTransfer();
        dt.items.add(file);
        this._proofInput.files = dt.files;
      } catch (e) { /* DataTransfer unsupported — proof simply not attached */ }
    };
    // Downscale to keep the order attachment small.
    const max = 800, scale = Math.min(1, max / this.W);
    const tw = Math.round(this.W * scale), th = Math.round(this.H * scale);
    const tmp = document.createElement('canvas'); tmp.width = tw; tmp.height = th;
    tmp.getContext('2d').drawImage(this.canvas, 0, 0, tw, th);
    if (sync || !tmp.toBlob) { make(this._dataURLtoBlob(tmp.toDataURL('image/jpeg', 0.85))); }
    else { tmp.toBlob(make, 'image/jpeg', 0.85); }
  }

  _dataURLtoBlob(dataURL) {
    const parts = dataURL.split(','), mime = parts[0].match(/:(.*?);/)[1];
    const bin = atob(parts[1]), len = bin.length, u8 = new Uint8Array(len);
    for (let i = 0; i < len; i++) u8[i] = bin.charCodeAt(i);
    return new Blob([u8], { type: mime });
  }

  /* ------------------------------------------------------------- 3D view */
  _setView(view) {
    this._queryAll('[data-wd-kc-view]').forEach((t) => {
      const active = t.dataset.wdKcView === view;
      t.classList.toggle('is-active', active);
      t.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
    this._queryAll('[data-wd-kc-view-pane]').forEach((p) => {
      p.hidden = p.dataset.wdKcViewPane !== view;
    });
    const hint = this._query('[data-wd-kc-3d-hint]');
    if (view === '3d') {
      if (hint) hint.hidden = false;
      this._start3D();
    } else {
      if (hint) hint.hidden = true;
      this._pause3D();
    }
  }

  async _start3D() {
    if (!this.config.three || !this.config.three.enabled) return;
    if (this._three) { this._resume3D(); return; }
    const mount = this._query('[data-wd-kc-3d-mount]');
    if (!mount) return;
    if (this._loading3D) return;
    this._loading3D = true;
    mount.classList.add('is-loading');
    try {
      const THREE = await import('https://esm.sh/three@0.162.0');
      const ctrlMod = await import('https://esm.sh/three@0.162.0/examples/jsm/controls/OrbitControls.js');
      const OrbitControls = ctrlMod.OrbitControls;

      const w = mount.clientWidth || 480, h = mount.clientHeight || 480;
      const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      renderer.setSize(w, h);
      mount.appendChild(renderer.domElement);

      const scene = new THREE.Scene();
      const camera = new THREE.PerspectiveCamera(35, w / h, 0.1, 100);
      camera.position.set(0, 0, 6);

      scene.add(new THREE.AmbientLight(0xffffff, 0.85));
      const key = new THREE.DirectionalLight(0xffffff, 0.9); key.position.set(2, 3, 4); scene.add(key);
      const rim = new THREE.DirectionalLight(0xffffff, 0.35); rim.position.set(-3, 1, -2); scene.add(rim);

      const tex = new THREE.CanvasTexture(this.canvas);
      tex.colorSpace = THREE.SRGBColorSpace;
      const material = new THREE.MeshStandardMaterial({ map: tex, roughness: 0.85, metalness: 0.0, side: THREE.DoubleSide });

      const controls = new OrbitControls(camera, renderer.domElement);
      controls.enableDamping = true;
      controls.enablePan = false;
      controls.enableZoom = window.innerWidth < 768;
      controls.minPolarAngle = Math.PI / 2 - 0.3;
      controls.maxPolarAngle = Math.PI / 2 + 0.3;

      this._three = { THREE, renderer, scene, camera, controls, material, mount };
      this._tex = tex;

      const modelUrl = this.config.three.model;
      // Apply our live recolour canvas as the model's texture when the model is
      // authored to our 2-zone master layout. For an external/realistic model
      // (its own UVs + textures), set three.recolor=false to show it as-is.
      const applyTex = this.config.three.recolor !== false;
      if (modelUrl) {
        try {
          const root = await this._loadModel(modelUrl);
          if (applyTex) root.traverse((c) => { if (c.isMesh) c.material = material; });
          this._frameObject(root);
          scene.add(root);
        } catch (e) {
          console.warn('[wd-kit-customizer] model load failed, using placeholder', e);
          scene.add(this._placeholderMesh());
        }
      } else {
        scene.add(this._placeholderMesh());
      }

      this._ro = new ResizeObserver(() => this._resize3D());
      this._ro.observe(mount);

      mount.classList.remove('is-loading');
      this._render3DLoop();
    } catch (e) {
      console.warn('[wd-kit-customizer] 3D unavailable', e);
      mount.classList.remove('is-loading');
      const hint = this._query('[data-wd-kc-3d-hint]');
      if (hint) { hint.hidden = false; hint.textContent = '3D preview unavailable on this device'; }
    }
    this._loading3D = false;
  }

  // Loads a model by file type and returns a THREE.Object3D root.
  // Supports .glb/.gltf (GLTFLoader), .fbx (FBXLoader) and .obj (OBJLoader),
  // so artist-made or AI-generated models drop in regardless of format.
  async _loadModel(url) {
    const base = 'https://esm.sh/three@0.162.0/examples/jsm/loaders/';
    const ext = (url.split('?')[0].split('#')[0].match(/\.([a-z0-9]+)$/i) || [, ''])[1].toLowerCase();
    if (ext === 'glb' || ext === 'gltf') {
      const m = await import(base + 'GLTFLoader.js');
      const g = await new m.GLTFLoader().loadAsync(url);
      return g.scene;
    }
    if (ext === 'fbx') {
      const m = await import(base + 'FBXLoader.js');
      return await new m.FBXLoader().loadAsync(url);
    }
    const m = await import(base + 'OBJLoader.js');
    return await new m.OBJLoader().loadAsync(url);
  }

  _placeholderMesh() {
    const THREE = this._three.THREE;
    // A tapered open cylinder reads as a jersey torso; the live canvas wraps it.
    const geo = new THREE.CylinderGeometry(1.15, 1.35, 2.6, 64, 1, true);
    const mesh = new THREE.Mesh(geo, this._three.material);
    mesh.rotation.y = Math.PI; // face the seam to the back
    return mesh;
  }

  _frameObject(obj) {
    const THREE = this._three.THREE;
    const box = new THREE.Box3().setFromObject(obj);
    const size = box.getSize(new THREE.Vector3());
    const center = box.getCenter(new THREE.Vector3());
    obj.position.sub(center);
    const maxDim = Math.max(size.x, size.y, size.z) || 1;
    const scale = 3 / maxDim;
    obj.scale.setScalar(scale);
  }

  _render3DLoop() {
    if (!this._three) return;
    this._raf3D = requestAnimationFrame(() => this._render3DLoop());
    this._three.controls.update();
    this._three.renderer.render(this._three.scene, this._three.camera);
  }

  _resize3D() {
    if (!this._three) return;
    const { renderer, camera, mount } = this._three;
    const w = mount.clientWidth || 480, h = mount.clientHeight || 480;
    renderer.setSize(w, h);
    camera.aspect = w / h; camera.updateProjectionMatrix();
  }

  _pause3D() { if (this._raf3D) { cancelAnimationFrame(this._raf3D); this._raf3D = null; } }
  _resume3D() { if (this._three && !this._raf3D) this._render3DLoop(); }
  _stop3D() {
    this._pause3D();
    this._ro?.disconnect();
    if (this._three) {
      const { renderer, scene, controls, material } = this._three;
      try {
        // renderer.dispose() does NOT free scene geometries/materials/textures.
        scene?.traverse((o) => {
          if (o.isMesh) {
            o.geometry?.dispose();
            const m = o.material;
            if (Array.isArray(m)) m.forEach((mm) => mm?.dispose()); else m?.dispose();
          }
        });
        material?.dispose();
        this._tex?.dispose();
        controls?.dispose();
        renderer.forceContextLoss();
        renderer.dispose();
        renderer.domElement.remove();
      } catch (e) {}
      this._three = null; this._tex = null;
    }
  }
}

if (!window.customElements.get('wd-kit-customizer')) {
  window.customElements.define('wd-kit-customizer', WdKitCustomizer);
}
